/**
 * Google Maps Adapter
 *
 * Wraps the Google Maps JavaScript SDK into a provider-agnostic facade.
 * Handles SDK loading, map creation, and exposes a stable interface for
 * map operations (camera, events, markers, overlays).
 *
 * @module libraries/googleMapsAdapter
 */

import createGoogleMapsLoader from './googleMapsLoader';
import markers from './mapLibrary/markers';
import overlays from "./mapLibrary/overlays";
import svgOverlays from "./mapLibrary/svgOverlays";
import kmlBounds from "./mapLibrary/kmlBounds";

function toLiteralPosition(value) {
  if (!value) {
    return null;
  }

  if (typeof value.lat === 'function') {
    return {
      lat: value.lat(),
      lng: value.lng(),
    };
  }

  return {
    lat: value.lat,
    lng: value.lng,
  };
}

function toBoundsLiteral(bounds) {
  if (!bounds) {
    return null;
  }

  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();

  return {
    north: northEast.lat(),
    east: northEast.lng(),
    south: southWest.lat(),
    west: southWest.lng(),
  };
}

export default function createGoogleMapsAdapter() {
  const state = {
    google: null,
    map: null,
    mapElement: null,
    listeners: new Set(),
  };

  let googleLoadPromise = null;
  const loadGoogleMaps = createGoogleMapsLoader({ getGlobal: () => window, getDocument: () => document });

  function assertMapReady() {
    if (!state.map || !state.google?.maps) {
      throw new Error('Map is not initialized. Call createMap first.');
    }
  }

  async function load({ config }) {
    if (!googleLoadPromise) {
      googleLoadPromise = loadGoogleMaps(
        {
          src: config?.src,
          onLoad: config?.onLoad,
          onError: config?.onError,
          onScriptInjected: config?.onScriptInjected
        }
      );
    }

    await googleLoadPromise;

    state.google = window.google;

    await state.google.maps.importLibrary('maps');
    await state.google.maps.importLibrary('marker');
  }

  function createMap({ container, options = {} } = {}) {
    if (!container) {
      throw new Error('Map container is required.');
    }

    state.mapElement = container;
    const finalOptions = {
      ...options,
      mapId: options.mapId,
    };

    state.map = new state.google.maps.Map(container, finalOptions);
    return state.map;
  }

  function onceMapEvent(eventName, handler) {
    assertMapReady();

    const listener = state.google.maps.event.addListenerOnce(
      state.map,
      eventName,
      handler
    );

    return () => listener.remove?.();
  }

  function onMapEvent(eventName, handler) {
    assertMapReady();

    const listener = state.google.maps.event.addListener(state.map, eventName, handler);
    const unsubscribe = () => listener.remove?.();

    state.listeners.add(unsubscribe);
    return () => {
      unsubscribe();
      state.listeners.delete(unsubscribe);
    };
  }

  function getCameraSnapshot() {
    assertMapReady();

    const center = state.map.getCenter?.();
    const bounds = state.map.getBounds?.();

    return {
      center: toLiteralPosition(center),
      bounds: toBoundsLiteral(bounds),
      zoom: state.map.getZoom?.() ?? 0,
    };
  }

  function getBounds() {
    assertMapReady();
    return toBoundsLiteral(state.map.getBounds?.());
  }

  function updateMapCenter({ latitude, longitude }) {
    assertMapReady();
    state.map.setCenter?.({
      lat: latitude,
      lng: longitude,
    });
  }

  function setMapOptions(options = {}) {
    assertMapReady();
    state.map.setOptions?.(options);
  }

  function setMapMinZoom(minZoom) {
    assertMapReady();
    setMapOptions({ minZoom });
  }

  function setMapZoom(zoom) {
    assertMapReady();
    setMapOptions({ zoom });
  }

  function setMapType(mapType) {
    assertMapReady();

    const mapTypeMap = {
      roadmap: state.google.maps.MapTypeId.ROADMAP,
      satellite: state.google.maps.MapTypeId.SATELLITE,
      terrain: state.google.maps.MapTypeId.TERRAIN,
      hybrid: state.google.maps.MapTypeId.HYBRID,
    };

    // Guard: validate mapType is supported
    if (!mapTypeMap[mapType]) {
      return;
    }

    const googleMapType = mapTypeMap[mapType];
    state.map.setMapTypeId?.(googleMapType);
  }

  function destroyMap() {
    Array.from(state.listeners).forEach((unsubscribe) => {
      unsubscribe?.();
    });
    state.listeners.clear();

    state.map = null;
    state.mapElement = null;
  }

  async function destroy() {
    destroyMap();
    state.google = null;
  }

  function createFacade() {
    const baseFacade = {
      provider: 'google-maps',
      capabilities: {
        advancedMarkers: true,
        overlays: true,
        kml: true,
      },

      createMap,
      destroyMap,
      onMapEvent,
      onceMapEvent,
      getCameraSnapshot,
      getBounds,
      updateMapCenter,
      setMapMinZoom,
      setMapZoom,
      setMapType,
    };

    return [overlays, svgOverlays, markers, kmlBounds].reduce((facade, mixin) => mixin(facade, state), baseFacade);
  }

  return {
    load,
    createFacade,
    destroy,
  };
}
